import { type ConnectWebviewsCreateParameters } from "@seamapi/http"
import { useSeamHttpClient } from "./index"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useWorkspaceOrganizationId } from "./use-workspaces"
import { buildClassicWorkspaceEntryPath } from "core/lib/classic-path"

export const useCreateConnectWebview = ({
  workspace_id,
}: {
  workspace_id: string
}) => {
  const qc = useQueryClient()
  const seam = useSeamHttpClient(workspace_id)
  // The webview can be created for a workspace other than the one in the URL,
  // so the organization comes from the workspace list rather than the route.
  const { organizationId } = useWorkspaceOrganizationId(workspace_id)

  return useMutation({
    mutationFn: async (opts: ConnectWebviewsCreateParameters) => {
      return seam?.connectWebviews.create({
        // Default the post-completion redirect to this workspace's devices page
        // so the webview's "Go to App" button returns the user to the console
        // instead of falling back to the seam.co marketing site (ENG-1809).
        // Callers can still override by passing custom_redirect_url in opts.
        custom_redirect_url: getWorkspaceDevicesUrl(
          organizationId,
          workspace_id
        ),
        ...opts,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connect_webviews", workspace_id] })
    },
  })
}

const getWorkspaceDevicesUrl = (
  organizationId: string | null,
  workspace_id: string
): string | undefined => {
  if (typeof window === "undefined") return undefined
  return `${window.location.origin}${buildClassicWorkspaceEntryPath({
    organizationId,
    workspaceId: workspace_id,
    subpath: "/devices",
  })}`
}
